import {
  forwardRef,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from "react";
import useTranslator from "../../hooks/useTranslator";
import { Tooltip as ReactTooltip } from "react-tooltip";
import Slider from "rc-slider";
import "rc-slider/assets/index.css";
import { getRentPercentage } from "../../helper/common";
import { Button, Card, Notice, TextField } from "../design";
import styles from "./TargetRent.module.css";

const TargetRent = forwardRef(
  (
    {
      monthlyIncome = 0,
      rent = null,
      onRentChange,
      onAddGuarantor,
      onAddFlatmate,
      hasGuarantor = false,
      hasFlatmate = false,
      disabled = false,
      processing = false,
      readOnly = false,
      isGuarantor = false,
      onSetTargetRent,
    },
    ref
  ) => {
    const { _t } = useTranslator();
    const min = 10;
    const max = 50;

    const hasRent = rent !== null && rent !== undefined;
    const inputRef = useRef(null);
    const [showRentOverlay, setShowRentOverlay] = useState(!hasRent);
    useImperativeHandle(ref, () => ({
      focus: () => {
        setShowRentOverlay(false);
        requestAnimationFrame(() => inputRef.current?.focus());
      },
    }));

    const effortRate = useMemo(() => {
      if (!monthlyIncome || !hasRent || !rent) return min;
      const p = getRentPercentage(monthlyIncome, rent)?.exactValue;
      return Math.min(max, Math.max(min, Math.round(p) || min));
    }, [monthlyIncome, rent, hasRent]);

    const comfortableMax = useMemo(
      () => Number(((monthlyIncome * 35) / 100).toFixed(2)),
      [monthlyIncome]
    );
    const tighterMax = useMemo(
      () => Number(((monthlyIncome * 45) / 100).toFixed(2)),
      [monthlyIncome]
    );
    const isPast = hasRent && rent > comfortableMax;
    const fmt = (n) =>
      new Intl.NumberFormat("de-DE", {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      }).format(n);
    const handleSliderChange = (val) => {
      if (!monthlyIncome) {
        return;
      }
      if (disabled || readOnly) return;
      onRentChange?.(Number(((monthlyIncome * val) / 100).toFixed(2)));
    };
    // Keep the amount label aligned with the slider thumb. The previous
    // calculation used the rent against a dynamically changing range, which
    // made the label drift away from the barometer after typing in the input.
    const percent = hasRent ? ((effortRate - min) / (max - min)) * 100 : 0;
    const disabledTooltip = readOnly
      ? _t("viewOnly", "View only — you can't make changes to this profile.")
      : processing
        ? _t(
            "profileProcessingWait",
            "Your profile is still being processed. Please wait until it's ready."
          )
        : disabled
          ? _t(
              "profileOutdatedRenewFirst",
              "Your profile is outdated. Please renew your profile first."
            )
          : undefined;

    const focusTargetRent = () => {
      setShowRentOverlay(false);
      onSetTargetRent?.();
      requestAnimationFrame(() => inputRef.current?.focus());
    };

    return (
      <Card
        className={`tenant-profile-placeholder-card tenant-profile-placeholder-card--target-rent ${styles.card}`}
      >
        <div
          className={
            !hasRent && showRentOverlay ? styles.lockedContent : undefined
          }
          aria-hidden={!hasRent && showRentOverlay}
        >
          <h2 className={styles.title}>
            {_t("yourRentalBarometer", "Your rental barometer")}
          </h2>
          <p className={styles.subtitle}>
            {!hasRent
              ? _t(
                  "rentalBarometerNotSet",
                  "Enter a rent to see where you stand for your income."
                )
              : isPast
                ? _t(
                    "rentalBarometerPastComfortable",
                    `At €${fmt(rent)}/month, you're past the comfortable range for your income.`,
                    undefined,
                    { rent: fmt(rent) }
                  )
                : _t(
                    "rentalBarometerComfortable",
                    `At €${fmt(rent)}/month, you're within the comfortable range for your income.`,
                    undefined,
                    { rent: fmt(rent) }
                  )}
          </p>
          <label className={styles.label}>
            {_t("enterRentConsidering", "Enter the rent you're considering")}
          </label>
          {disabled ? (
            <div
              style={{ display: "inline-block" }}
              data-tooltip-id="target-rent-input-tooltip"
            >
              <div style={{ maxWidth: 240 }}>
                <TextField
                  id="target-rent-input"
                  ref={inputRef}
                  type="number"
                  icon="€"
                  trailing="/month"
                  value={rent ?? ""}
                  min={0}
                  disabled={disabled}
                  onChange={(e) => {
                    if (disabled) return;
                    onRentChange?.(Math.max(0, Number(e.target.value) || 0));
                  }}
                  aria-label={_t("targetRent", "Target Rent")}
                  style={{ cursor: "not-allowed" }}
                  placeholder="0"
                />
              </div>
              <ReactTooltip
                id="target-rent-input-tooltip"
                place="top"
                className="react-tooltip-styling"
                render={() => <p>{disabledTooltip}</p>}
              />
            </div>
          ) : (
            <div style={{ maxWidth: 240 }}>
              <TextField
                id="target-rent-input"
                ref={inputRef}
                type="number"
                icon="€"
                trailing="/month"
                value={rent ?? ""}
                min={0}
                disabled={readOnly}
                onChange={(e) => {
                  if (readOnly) return;
                  onRentChange?.(Math.max(0, Number(e.target.value) || 0));
                }}
                aria-label={_t("targetRent", "Target Rent")}
                placeholder="0"
              />
            </div>
          )}
          <div className={styles.meterWrap}>
            <span
              className={styles.valueLabel}
              style={{
                left: `${percent}%`,
                "--anchor": `${-percent}%`,
              }}
            >
              {hasRent ? `€${fmt(rent)}` : ""}
            </span>
            <div
              className={`skormeter ${styles.meter}`}
              style={{ "--w": ((effortRate - min) / (max - min)) * 100 }}
            >
              {disabled ? (
                <div
                  style={{ display: "block" }}
                  data-tooltip-id="target-rent-slider-tooltip"
                >
                  <Slider
                    min={min}
                    max={max}
                    value={effortRate}
                    onChange={handleSliderChange}
                    disabled
                  />
                  <ReactTooltip
                    id="target-rent-slider-tooltip"
                    place="top"
                    className="react-tooltip-styling"
                    render={() => <p>{disabledTooltip}</p>}
                  />
                </div>
              ) : (
                <Slider
                  min={min}
                  max={max}
                  value={effortRate}
                  onChange={handleSliderChange}
                  disabled={!monthlyIncome || readOnly}
                />
              )}
              <div className="slider-labels">
                <span>{_t("comfortable", "Comfortable")}</span>
                <span></span>
                <span>{_t("moderate", "Moderate")}</span>
                <span></span>
                <span>{_t("elevated", "Elevated")}</span>
              </div>
            </div>
          </div>
          <div className={styles.scale}>
            <span>
              {_t("comfortableUpTo", "Comfortable · up to")} €
              {fmt(comfortableMax)}
            </span>
            <span>
              {_t("tighterAbove", "Tighter · above")} €{fmt(comfortableMax)}
              {tighterMax > comfortableMax
                ? ` · up to €${fmt(tighterMax)}`
                : ""}
            </span>
          </div>

          {!isGuarantor &&
            !readOnly &&
            isPast &&
            !(hasGuarantor && hasFlatmate) && (
              <Notice tone="warning" className={styles.callout}>
                <p className={styles.calloutText}>
                  {_t(
                    "rentalBarometerCallout",
                    "A guarantor or flatmate can help offset this and keep your application strong."
                  )}
                </p>
                <div className={styles.actions}>
                  {!hasGuarantor &&
                    (disabled ? (
                      <div
                        style={{ display: "inline-block" }}
                        data-tooltip-id="target-rent-guarantor-tooltip"
                      >
                        <Button
                          size="sm"
                          onClick={onAddGuarantor}
                          disabled={disabled}
                        >
                          {_t("addGuarantor", "Add guarantor")}
                        </Button>
                        <ReactTooltip
                          id="target-rent-guarantor-tooltip"
                          place="top"
                          className="react-tooltip-styling"
                          render={() => <p>{disabledTooltip}</p>}
                        />
                      </div>
                    ) : (
                      <Button size="sm" onClick={onAddGuarantor}>
                        {_t("addGuarantor", "Add guarantor")}
                      </Button>
                    ))}
                  {!hasFlatmate &&
                    (disabled ? (
                      <div
                        style={{ display: "inline-block" }}
                        data-tooltip-id="target-rent-flatmate-tooltip"
                      >
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={onAddFlatmate}
                          disabled={disabled}
                        >
                          {_t("addFlatmate", "Add flatmate")}
                        </Button>
                        <ReactTooltip
                          id="target-rent-flatmate-tooltip"
                          place="top"
                          className="react-tooltip-styling"
                          render={() => <p>{disabledTooltip}</p>}
                        />
                      </div>
                    ) : (
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={onAddFlatmate}
                      >
                        {_t("addFlatmate", "Add flatmate")}
                      </Button>
                    ))}
                </div>
              </Notice>
            )}
        </div>
        {!hasRent && showRentOverlay && (
          <div className={styles.rentOverlay}>
            <div className={styles.rentOverlayContent}>
              <strong>
                {_t("targetRentNotSetTitle", "Set your target rent")}
              </strong>
              <span>
                {_t(
                  "targetRentNotSetDescription",
                  "Add the rent you're considering to see your rental barometer."
                )}
              </span>
              <Button
                className={styles.overlayButton}
                onClick={focusTargetRent}
                disabled={disabled || readOnly}
                style={readOnly ? { display: "none" } : undefined}
              >
                {_t("setTargetRent", "Set")}
              </Button>
            </div>
          </div>
        )}
      </Card>
    );
  }
);

export default TargetRent;
